import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  language: "en",
  mode: "dark",
  timemode: "UTC",
  isSidebarOpen: false,
  isChatOpen: false,
  startDate: null,
  endDate: null,
  user: null,
  token: null,
  params: [],
  crewRanks: [],
  flights: [],
  crewRoutes: [],
  job255CrewRoutes: [],
  crewBlockHours: [],
  notifications: [],
  posts: [],
  conversations: [],
  unreadCount: 0,
  isAICopilotOpen: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
    setMode: (state) => {
      state.mode = state.mode === "dark" ? "light" : "dark";
    },
    setTimeMode: (state) => {
      state.timemode = state.timemode === "UTC" ? "LocalBase" : "UTC";
    },
    setOpenSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    toggleChat: (state) => {
      state.isChatOpen = !state.isChatOpen;
    },
    setChatOpen: (state, action) => {
      state.isChatOpen = action.payload;
    },
    toggleAICopilot: (state) => {
      state.isAICopilotOpen = !state.isAICopilotOpen;
    },
    setAICopilotOpen: (state, action) => {
      state.isAICopilotOpen = action.payload;
    },
    setLogin: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      if (state.user.preferences) {
        const preferences = state.user.preferences;
        state.language = preferences.language || "en";
        state.mode = preferences.themeMode || "dark";
        state.timemode = preferences.timeMode || "UTC";
        state.isSidebarOpen = preferences.isSidebarOpen || false;
        state.startDate = preferences.startDate || null;
        state.endDate = preferences.endDate || null;        
      }
      state.params = action.payload.params;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setLogout: (state) => {
      state.token = null;
      state.user = null;
      state.params = {};
      state.crewRanks = [];
    },
    setCrewRanks: (state, action) => {
      state.crewRanks = action.payload.crewRanks;
    },
    setFlights: (state, action) => {
      state.flights = Array.isArray(action.payload.flights)
        ? action.payload.flights
        : [];
    },
    appendFlights: (state, action) => {
      if (Array.isArray(action.payload.flights)) {
        state.flights = state.flights.concat(action.payload.flights);
      }
    },
    setCrewRoutes: (state, action) => {
      state.crewRoutes = action.payload.crewRoutes;
    },
    setJob255CrewRoutes: (state, action) => {
      state.job255CrewRoutes = action.payload.job255CrewRoutes;
    },
    setCrewBlockHours: (state, action) => {
      state.crewBlockHours = action.payload.crewBlockHours;
    },
    setNotifications: (state, action) => {
      state.notifications = Array.isArray(action.payload) ? action.payload : [];
    },
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(n => n._id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setConversations: (state, action) => {
      state.conversations = Array.isArray(action.payload) ? action.payload : [];
    },
    addOrUpdateConversation: (state, action) => {
      const idx = state.conversations.findIndex((c) => c._id === action.payload._id);
      if (idx >= 0) state.conversations[idx] = action.payload;
      else state.conversations.unshift(action.payload);
    },
    removeConversation: (state, action) => {
      state.conversations = state.conversations.filter((c) => c._id !== action.payload);
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    decrementUnread: (state, action) => {
      state.unreadCount = Math.max(0, state.unreadCount - (action.payload || 1));
    },
    setPosts: (state, action) => {
      state.posts = Array.isArray(action.payload.posts) ? action.payload.posts : []
    },
    setPost: (state, action) => {
      const updatedPosts = state.posts.map((post) => {
        if (post._id === action.payload.post._id) return action.payload.post;
        return post;
      });
      state.posts = updatedPosts;
    }, 
    setFriends: (state, action) => {
      if (state.user) {
        state.user.friends = action.payload.friends;
      } else {
        console.error("User friends non-existent :(");
      }
    },
  },
});

export const {
  setLanguage,
  setMode,
  setTimeMode,
  setOpenSidebar,
  setLogin,
  setToken,
  setLogout,
  setCrewRanks,
  setFlights,
  appendFlights,
  setCrewRoutes,
  setJob255CrewRoutes,
  setCrewBlockHours,
  setNotifications,
  addNotification,
  removeNotification,
  clearNotifications,
  toggleChat,
  setChatOpen,
  toggleAICopilot,
  setAICopilotOpen,
  setConversations,
  addOrUpdateConversation,
  removeConversation,
  setUnreadCount,
  decrementUnread,
  setPosts,
  setPost,
  setFriends,
} = authSlice.actions;
  
export default authSlice.reducer;
